/**
 * API client for the rebuilt app.
 *
 * One thin layer over fetch, talking to the Workers/D1 API. It differs
 * from the old src/config/api.js in that it exposes ONLY endpoints that
 * actually exist on the server — no stubs, no methods that 404 at
 * runtime. If something isn't here, the backend doesn't have it.
 *
 * Contract (sysv2/API/src/lib/db.js):
 *   ok   -> { success: true, data }
 *   fail -> { success: false, error: { code, message } }
 */
import { noteDataVersion, setVersionFetcher } from './cache/cacheStore';
import { isOnline, reportNetworkFailure, reportNetworkSuccess } from './cache/useNetworkStatus';
import { getSnapshot, putSnapshot } from './offline/snapshotStore';
import { waitForOnline } from './offline/connection';
import {
  enqueue, attempt, settle, noteSnapshotServed, clearSnapshotServed,
} from './offline/outbox';

const BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8787') + '/api/v1';

// The login screen is read by parents and teachers; the API's English
// messages are for developers. ACCOUNT_LOCKED is deliberately absent:
// its message is the lock reason the office typed, already in Arabic.
const AUTH_MESSAGES = {
  INVALID_CODE: 'الرمز غير صحيح',
  RATE_LIMIT_EXCEEDED: 'محاولات خاطئة كثيرة. انتظر ١٥ دقيقة ثم حاول مجدداً.',
  NOT_FOUND: 'لا يوجد حساب بهذا الاسم. تأكد من الاسم ونوع الحساب، أو راجع إدارة المدرسة.',
  SMS_PROVIDER_ERROR: 'تعذّر إرسال الرمز، حاول مرة أخرى.',
  // Only on the verify step does VALIDATION_ERROR mean "no live code".
  'login-verify:VALIDATION_ERROR': 'انتهت صلاحية الرمز، ارجع واطلب رمزاً جديداً.',
};
const authMessage = (path, code) =>
  AUTH_MESSAGES[`${path.split('/').pop()}:${code}`] || AUTH_MESSAGES[code];

const TOKEN_KEY = 'mtzedu_session_token';
const USER_KEY = 'mtzedu_session_user';

function clearImportDrafts() {
  if (typeof sessionStorage !== 'undefined') {
    Object.keys(sessionStorage).filter((key) => key.startsWith('mtz.importDraft.') || key.startsWith('mtz.dossierDraft.')).forEach((key) => sessionStorage.removeItem(key));
  }
}

export const session = {
  token: () => localStorage.getItem(TOKEN_KEY),
  user: () => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  },
  save: (token, user) => {
    clearImportDrafts();
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear: () => {
    clearImportDrafts();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

const newRequestId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);

// Reads that must fail fast when offline instead of answering from a
// saved copy or waiting: the freshness check is only meaningful live.
const NO_SNAPSHOT = new Set(['/data-version']);
const snapKey = (path) => `${session.user()?.id ?? 'anon'}:${path}`;

function offlineError(cause) {
  const e = new Error('تعذّر الاتصال بالخادم');
  e.cause = cause;
  return e;
}

/**
 * The network part of a request, with the offline rules
 * (ui/offline/*):
 *
 *   READ  (GET)  online → the server, and the answer is saved.
 *                offline → the last saved answer for this exact URL,
 *                so every screen, list and search keeps working. Never
 *                seen before → wait for the connection, then load.
 *   WRITE        offline → NOT failed. It waits in the outbox (the
 *                form stays open, nothing is lost, no reload) and goes
 *                out when the connection returns, with the same
 *                X-Request-Id every time so the server applies it once.
 *   /auth/*      never waits and never uses saved copies: a login
 *                code must not be replayed, and logging out offline
 *                must still log out locally.
 */
async function send(path, method, headers, body, snapshot = true) {
  const url = `${BASE}${path}`;
  const init = { method, headers, body: body === undefined ? undefined : JSON.stringify(body) };
  const isAuth = path.startsWith('/auth/');

  // Imports own their durable journal. Never queue a commit behind an offline
  // form or replay a saved matching snapshot; the wizard reconciles outcomes.
  // Questions to «اسأل مشكاة» (/ai, /assistant) are never queued either: a
  // question answered tomorrow is no answer.
  if (isAuth || path.startsWith('/student-imports') || path.startsWith('/ai/') || path.startsWith('/assistant/')) {
    try { return await fetch(url, init); } catch (e) { reportNetworkFailure(); throw offlineError(e); }
  }

  if (method === 'GET') {
    for (;;) {
      try {
        return await fetch(url, init);
      } catch (e) {
        reportNetworkFailure();
        if (!snapshot || NO_SNAPSHOT.has(path)) throw offlineError(e);
        const snap = await getSnapshot(snapKey(path));
        if (snap) {
          noteSnapshotServed(snap.at);
          return { snapshot: snap.data };
        }
        await waitForOnline();
      }
    }
  }

  const id = newRequestId();
  headers['X-Request-Id'] = id;
  let entry = null;
  try {
    for (;;) {
      if (!isOnline()) {
        entry = entry || enqueue(id, method, path);
        await Promise.race([waitForOnline(), entry.cancelled]);
      }
      try {
        if (entry) attempt(id);
        return await fetch(url, init);
      } catch (e) {
        reportNetworkFailure();
        entry = entry || enqueue(id, method, path);
      }
    }
  } finally {
    if (entry) settle(id);
  }
}

// `snapshot: false` — a read that is neither saved for offline use nor
// answered from a saved copy (a poll whose URL changes every call).
async function request(path, { method = 'GET', body, auth = true, snapshot = true } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const t = session.token();
    if (t) headers.Authorization = `Bearer ${t}`;
  }

  const res = await send(path, method, headers, body, snapshot);
  if (res.snapshot !== undefined) return res.snapshot;
  reportNetworkSuccess();

  const payload = await res.json().catch(() => null);

  // A write answers with the new data version: our own change must
  // never be followed by a cached copy of the old data.
  noteDataVersion(res.headers.get('X-Data-Version'));

  if (!res.ok || !payload || payload.success === false) {
    // A dead or rejected session must drop the user back to login
    // rather than leaving every screen showing a vague failure. Only
    // for calls that CARRIED a session: a wrong code on the login form
    // is also a 401, and reloading there threw the user back to step 1.
    if (res.status === 401 && auth && headers.Authorization) {
      session.clear();
      if (typeof window !== 'undefined') window.location.reload();
    }
    const serverMessage = payload?.error?.message;
    const err = new Error(
      (path.startsWith('/auth/') && authMessage(path, payload?.error?.code)) || serverMessage || `فشل الطلب (${res.status})`
    );
    err.status = res.status;
    err.code = payload?.error?.code;
    throw err;
  }

  if (method === 'GET' && auth && snapshot && !NO_SNAPSHOT.has(path)) {
    putSnapshot(snapKey(path), payload.data);
    clearSnapshotServed();
  } else if (method !== 'GET' && typeof window !== 'undefined') {
    // «ورّيني» (app/help/Coach.js) moves on when the save it waits for lands.
    window.dispatchEvent(new CustomEvent('mtz:saved', { detail: { method, path } }));
  }
  return payload.data;
}

/** Binary download/upload for backup files — same auth, no JSON. */
async function rawRequest(path, { method = 'GET', body, contentType } = {}) {
  const headers = {};
  const t = session.token();
  if (t) headers.Authorization = `Bearer ${t}`;
  if (contentType) headers['Content-Type'] = contentType;
  let res;
  try {
    res = await fetch(`${BASE}${path}`, { method, headers, body });
  } catch (networkError) {
    throw new Error('تعذّر الاتصال بالخادم');
  }
  noteDataVersion(res.headers.get('X-Data-Version'));
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    if (res.status === 401 && t) { session.clear(); window.location.reload(); }
    throw new Error(payload?.error?.message || `فشل الطلب (${res.status})`);
  }
  return res;
}

const qs = (params = {}) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return entries.length ? `?${new URLSearchParams(entries)}` : '';
};

const api = {
  // Excel import: many small requests, put together in pages/import/importClient.js.
  importContext: () => request('/student-imports/context', { snapshot: false }),
  importContextRows: (table, after) => request(`/student-imports/context/${table}?after=${after}`, { snapshot: false }),
  importAssistLabels: (tasks) => request('/student-imports/assist/labels', { method: 'POST', body: { tasks }, snapshot: false }),
  importRuns: () => request('/student-imports', { snapshot: false }),
  importCreate: (body) => request('/student-imports', { method: 'POST', body, snapshot: false }),
  importOperations: (id, body) => request(`/student-imports/${id}/operations`, { method: 'POST', body, snapshot: false }),
  importRun: (id) => request(`/student-imports/${id}`, { snapshot: false }),
  importRunOperations: (id, after) => request(`/student-imports/${id}/operations?after=${after}`, { snapshot: false }),
  importCommit: (id, revision) => request(`/student-imports/${id}/commit`, { method: 'POST', body: { revision }, snapshot: false }),
  importStep: (id, revision) => request(`/student-imports/${id}/step`, { method: 'POST', body: { revision }, snapshot: false }),
  importPause: (id) => request(`/student-imports/${id}/pause`, { method: 'POST', snapshot: false }),
  // --- auth ---------------------------------------------------------
  loginLookup: (name, role, parent_type = 'none') =>
    request('/auth/login-lookup', { method: 'POST', body: { name, role, parent_type }, auth: false }),
  loginSelect: (user_id) =>
    request('/auth/login-select', { method: 'POST', body: { user_id }, auth: false }),
  loginVerify: (user_id, code, device_label) =>
    request('/auth/login-verify', { method: 'POST', body: { user_id, code, device_label }, auth: false }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  // Welcome intro: the panel version this account last saw it for.
  introStatus: () => request('/auth/intro', { snapshot: false }),
  markIntroSeen: (version) => request('/auth/intro', { method: 'POST', body: { version } }),
  // The welcome version and/or the daily tour's progress: { version?, tips? }.
  saveIntro: (body) => request('/auth/intro', { method: 'POST', body }),

  // "Has anything changed since my cache was filled?" — see cacheStore.
  dataVersion: () => request('/data-version').then((d) => d.version),

  // Navbar search: every student/section/teacher/driver/family the user
  // may open, in one role-scoped payload (api/src/routes/search.js).
  searchIndex: () => request('/search/index'),

  // لوحة المراقبة (api/src/routes/monitor.js). The feed is polled with a
  // moving `since`, so it is never snapshotted.
  // صفوفي — the teacher's home (api/src/routes/teacherHome.js).
  teacherHome: () => request('/teacher/home'),

  monitorSummary: () => request('/monitor/summary'),
  monitorFeed: (params) => request(`/monitor/feed${qs(params)}`, { snapshot: false }),

  // --- structure -----------------------------------------------------
  batches: () => request('/batches'),
  createBatch: (b) => request('/batches', { method: 'POST', body: b }),
  updateBatch: (id, b) => request(`/batches/${id}`, { method: 'PATCH', body: b }),

  grades: () => request('/grades'),
  createGrade: (g) => request('/grades', { method: 'POST', body: g }),
  updateGrade: (id, g) => request(`/grades/${id}`, { method: 'PATCH', body: g }),
  deleteGrade: (id) => request(`/grades/${id}`, { method: 'DELETE' }),

  subjects: () => request('/subjects'),
  createSubject: (s) => request('/subjects', { method: 'POST', body: s }),
  updateSubject: (id, s) => request(`/subjects/${id}`, { method: 'PATCH', body: s }),
  deleteSubject: (id) => request(`/subjects/${id}`, { method: 'DELETE' }),

  academicYears: () => request('/academic-years'),
  createAcademicYear: (y) => request('/academic-years', { method: 'POST', body: y }),
  updateAcademicYear: (id, y) => request(`/academic-years/${id}`, { method: 'PATCH', body: y }),

  sections: () => request('/sections'),
  section: (id) => request(`/sections/${id}`),
  createSection: (s) => request('/sections', { method: 'POST', body: s }),
  updateSection: (id, s) => request(`/sections/${id}`, { method: 'PATCH', body: s }),
  deleteSection: (id) => request(`/sections/${id}`, { method: 'DELETE' }),

  // --- people --------------------------------------------------------
  teachers: () => request('/teachers'),
  teacher: (id) => request(`/teachers/${id}`),
  createTeacher: (t) => request('/teachers', { method: 'POST', body: t }),
  updateTeacher: (id, t) => request(`/teachers/${id}`, { method: 'PATCH', body: t }),
  deleteTeacher: (id) => request(`/teachers/${id}`, { method: 'DELETE' }),

  students: (params) => request(`/students${qs(params)}`),
  // Whole-school directory for the شؤون الطلاب screen: every student with
  // their family's phones, today's attendance state and fee balance, in
  // one request (see people.js studentDirectory for why).
  studentDirectory: (params) => request(`/students/directory${qs(params)}`),
  // شؤون الطلاب → «الإضبارات»: pupils whose dossier was opened in [from, to).
  studentDossiers: (params) => request(`/students/dossiers${qs(params)}`),
  // The pupil's + parents' logins with their رمز الدخول (hint only) — «حسابات الدخول».
  studentLogins: (id) => request(`/students/${id}/logins`),
  student: (id) => request(`/students/${id}`),
  createStudent: (s) => request('/students', { method: 'POST', body: s }),
  updateStudent: (id, s) => request(`/students/${id}`, { method: 'PATCH', body: s }),
  deleteStudent: (id, body) => request(`/students/${id}`, { method: 'DELETE', body }),
  reactivateStudent: (id, body) => request(`/students/${id}/reactivate`, { method: 'POST', body }),
  studentAttendance: (id, params) => request(`/students/${id}/attendance${qs(params)}`),
  studentReportCard: (id) => request(`/students/${id}/report-card`),
  studentPaymentSummary: (id) => request(`/students/${id}/payment-summary`),
  setStudentFamily: (id, family_id) => request(`/students/${id}/family`, { method: 'PATCH', body: { family_id } }),
  setStudentTransportation: (id, transportation_id) =>
    request(`/students/${id}/transportation`, { method: 'PATCH', body: { transportation_id } }),

  families: (params) => request(`/families${qs(params)}`),
  family: (id) => request(`/families/${id}`),
  createFamily: (f) => request('/families', { method: 'POST', body: f }),
  updateFamily: (id, f) => request(`/families/${id}`, { method: 'PATCH', body: f }),
  familyStudents: (id) => request(`/families/${id}/students`),

  // --- enrollments ----------------------------------------------------
  enrollments: (params) => request(`/enrollments${qs(params)}`),
  createEnrollment: (e) => request('/enrollments', { method: 'POST', body: e }),
  transferStudent: (body) => request('/students/transfer', { method: 'POST', body }),

  // --- assignments -----------------------------------------------------
  assignments: () => request('/assignments'),
  createAssignment: (a) => request('/assignments', { method: 'POST', body: a }),
  swapTeacher: (id, new_teacher_id, reason) =>
    request(`/assignments/${id}/swap-teacher`, { method: 'POST', body: { new_teacher_id, reason } }),
  assignmentHistory: (id) => request(`/assignments/${id}/history`),

  // --- attendance --------------------------------------------------------
  sectionRoster: (id, date) => request(`/sections/${id}/roster${qs({ date })}`),
  sectionAttendanceHistory: (id, params) => request(`/sections/${id}/attendance-history${qs(params)}`),
  sectionAttendanceChanges: (id, params) => request(`/sections/${id}/attendance-changes${qs(params)}`),
  sectionAttendanceSummary: (id, params) => request(`/sections/${id}/attendance-summary${qs(params)}`),
  takeAttendance: (body) => request('/attendance', { method: 'POST', body }),
  updateAttendanceMark: (sectionId, date, studentId, body) =>
    request(`/attendance/${sectionId}/${date}/${studentId}`, { method: 'PATCH', body }),

  // --- exams ---------------------------------------------------------------
  exams: (params) => request(`/exams${qs(params)}`),
  exam: (id) => request(`/exams/${id}`),
  createExam: (e) => request('/exams', { method: 'POST', body: e }),
  updateExam: (id, e) => request(`/exams/${id}`, { method: 'PATCH', body: e }),
  deleteExam: (id) => request(`/exams/${id}`, { method: 'DELETE' }),
  publishExam: (id) => request(`/exams/${id}/publish`, { method: 'POST' }),
  unpublishExam: (id) => request(`/exams/${id}/unpublish`, { method: 'POST' }),
  examResults: (examId) => request(`/exams/${examId}/results`),
  createExamResult: (examId, body) => request(`/exams/${examId}/results`, { method: 'POST', body }),
  bulkCreateExamResults: (examId, results) =>
    request(`/exams/${examId}/results/bulk`, { method: 'POST', body: { results } }),
  updateExamResult: (examId, resultId, body) =>
    request(`/exams/${examId}/results/${resultId}`, { method: 'PATCH', body }),
  deleteExamResult: (examId, resultId) =>
    request(`/exams/${examId}/results/${resultId}`, { method: 'DELETE' }),

  // The school-wide list of exam kinds ("مذاكرة أولى", "إملاء 1"...).
  // An exam points at one of these, so the same kind means the same
  // thing in every section and a transcript can group on it.
  examTypes: (params) => request(`/exam-types${qs(params)}`),
  createExamType: (t) => request('/exam-types', { method: 'POST', body: t }),
  updateExamType: (id, t) => request(`/exam-types/${id}`, { method: 'PATCH', body: t }),
  deleteExamType: (id) => request(`/exam-types/${id}`, { method: 'DELETE' }),

  // --- money -----------------------------------------------------------------
  priceProfiles: (params) => request(`/price-profiles${qs(params)}`),
  createPriceProfile: (p) => request('/price-profiles', { method: 'POST', body: p }),
  priceProfilesBySection: (id) => request(`/price-profiles/section/${id}`),
  payments: (params) => request(`/payments${qs(params)}`),
  createPayment: (p) => request('/payments', { method: 'POST', body: p }),
  overdueStudents: () => request('/payments/overdue'),

  // --- transportation ----------------------------------------------------------
  transportation: () => request('/transportation'),
  createTransportation: (t) => request('/transportation', { method: 'POST', body: t }),
  updateTransportation: (id, t) => request(`/transportation/${id}`, { method: 'PUT', body: t }),

  // --- users -------------------------------------------------------------------
  // --- phone verification (optional, in every phone field) ------------------
  phoneVerifySend: (phone) => request('/phone-verify/send', { method: 'POST', body: { phone } }),
  phoneVerifyCheck: (phone, code) => request('/phone-verify/check', { method: 'POST', body: { phone, code } }),
  phoneVerifyStatus: (phones) => request(`/phone-verify/status${qs({ phones: phones.join(',') })}`),

  // --- backups (النسخ الاحتياطي) — admin -------------------------------------
  backups: () => request('/backups'),
  // الخدمات المرتبطة — Cloudflare / OneSignal / ClickSend / SMS (admin).
  servicesStatus: () => request('/system/services', { snapshot: false }),
  // المواقع والتطبيقات — visitors, traffic, crawlers, checks (admin).
  sitesStatus: () => request('/system/sites', { snapshot: false }),
  runPagespeed: (host, strategy) => request('/system/sites/pagespeed', { method: 'POST', body: { host, strategy } }),
  // «روّاد: حماس الطلاب» — what pupils want first in the student app (admin).
  rowadReport: () => request('/rowad/report', { snapshot: false }),
  createBackup: () => request('/backups', { method: 'POST', body: {} }),
  freshStart: () => request('/system/fresh-start', { method: 'POST', body: { confirm: 'FRESH_START' } }),
  restoreBackup: (name) => request(`/backups/${encodeURIComponent(name)}/restore`, { method: 'POST', body: { confirm: 'RESTORE' } }),
  /** Fetch a backup file and hand it to the browser as a download. */
  downloadBackup: async (name) => {
    const res = await rawRequest(`/backups/${encodeURIComponent(name)}/download`);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `mtzedu-${name}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  uploadBackup: async (file) => (await (await rawRequest('/backups/upload', {
    method: 'POST', body: file, contentType: 'application/octet-stream',
  })).json()).data,

  users: () => request('/users'),
  // Office accounts only (مدير / موجه) — see api/src/routes/users.js.
  createUser: (u) => request('/users', { method: 'POST', body: u }),
  updateUser: (id, u) => request(`/users/${id}`, { method: 'PATCH', body: u }),
  // سجل العمليات: read-only, newest first.
  auditLog: (params) => request(`/audit-log${qs(params)}`),
  lockUser: (id, reason) => request(`/users/${id}/lock`, { method: 'POST', body: { reason } }),
  unlockUser: (id) => request(`/users/${id}/unlock`, { method: 'POST' }),
  createIdentity: (body) => request('/auth/dev-create-identity', { method: 'POST', body }),

  // --- access codes (رمز الدخول) + login history ----------------------------------
  // The plain code comes back from issueAccessCode ONCE and is never
  // retrievable again — show it to the manager immediately.
  accessCodes: () => request('/access-codes'),
  issueAccessCode: (userId, note) => request(`/users/${userId}/access-code`, { method: 'POST', body: { note } }),
  revokeAccessCode: (userId) => request(`/users/${userId}/access-code/revoke`, { method: 'POST' }),
  loginHistory: (userId) => request(`/users/${userId}/login-history`),

  // --- notifications ---------------------------------------------------------------
  notifications: (params) => request(`/notifications${qs(params)}`),
  sendToStudent: (body) => request('/notifications/send-to-student', { method: 'POST', body }),
  sendToSection: (body) => request('/notifications/send-to-section', { method: 'POST', body }),
  markRead: (id) => request(`/notifications/${id}/read`, { method: 'PATCH' }),
  markAllRead: () => request('/notifications/mark-all-read', { method: 'PATCH' }),

  // The send log (السجل). For a teacher the server returns only their own
  // sends and their classes' slice of anyone else's — see
  // api/src/routes/notifications.js listBatches.
  notificationBatches: (params) => request(`/notifications/batches${qs(params)}`),
  notificationBatch: (id) => request(`/notifications/batches/${id}`),
  estimateAudience: (body) => request('/notifications/estimate', { method: 'POST', body }),
  sendNotification: (body) => request('/notifications/send', { method: 'POST', body }),

  // «اسأل مشكاة» (app/help/Assistant.js): a question about using the system.
  askAssistant: (body) => request('/assistant/ask', { method: 'POST', body }),
  assistantLog: () => request('/assistant/log?limit=80', { snapshot: false }),
  // Data questions: Workers AI picks a read-only query (api/src/routes/ai.js).
  askData: (body) => request('/ai/ask', { method: 'POST', body }),
  runData: (body) => request('/ai/run', { method: 'POST', body }),
};

// The cache asks this before trusting what it holds (cacheStore.js).
setVersionFetcher(() => (session.token() ? api.dataVersion() : Promise.resolve(null)));

export default api;
